import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Button, Modal, Space, TableColumnsType, Tag } from 'antd';

import { AuthRoute, AuthRoutes } from 'components/_Common/_Routes/Routes';
import List from 'components/_Common/List';
import dayjs from 'dayjs';
import { isBefore } from 'lib/compareDates';
import { getIssueDerivedStatus, getIssueDerivedStatusTagProps } from 'lib/helpers/Issue.helper';
import { useOverview } from 'lib/providers/OverviewContextProvider';

const makeUrlWithParameter = (route: AuthRoute, parameter: string, value: string): string => {
    const url = `${route}?${parameter}=${value}`;

    return url;
};

const COLUMNS: TableColumnsType<any> = [
    {
        title: 'Condomínio',
        dataIndex: ['client', 'name'],
        key: 'clientName',
    },
    {
        title: 'Status',
        dataIndex: 'status',
        render: (status, issue) => {
            const { status: derivedStatus, isOverdue } = getIssueDerivedStatus(status, issue.deadline);
            const tags = getIssueDerivedStatusTagProps(derivedStatus, isOverdue);
    
            return (
                <Space size="small">
                    {tags.map((tag, index) => (
                        <Tag key={index} color={tag.color} icon={tag.icon}>
                            {tag.text}
                        </Tag>
                    ))}
                </Space>
            );
        },
        width: 200
    },
    {
        title: 'Responsável',
        dataIndex: ['user_responsible', 'name'],
        key: 'userResponsible',
    },
    {
        title: 'Assunto',
        dataIndex: 'subject'
    },
    {
        title: 'Categoria',
        dataIndex: 'issue_type_id',
        render: (_, record) => record.issue_type.name
    },
    {
        title: 'Prazo',
        dataIndex: 'deadline',
        render: (value) => value !== null
            ? dayjs(value).format('DD/MM/YYYY')
            : '-',
        width: 110
    },
    {
        title: 'Ações',
        key: 'actions',
        render: (_, item) => (
            <Button href={makeUrlWithParameter(AuthRoutes.overviewIssue, 'detail', item.id.toString())} target='_blank'>
                Detalhes
            </Button>
        ),
    }
];

interface IssueModalProps {
    title: string;
    linkText: string;
    linkUrl: string;
    data: any[]; 
    columns: any[];
    onClose: () => void;
}

export function IssueModal({ title, linkText, linkUrl, data, columns, onClose }: IssueModalProps) {
    return (
        <Modal
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginRight: 20 }}>
                    <span>{title}</span>
                    <Link to={linkUrl}>{linkText}</Link>
                </div>
            }
            footer={null}
            onCancel={onClose}
            width={1100}
            open
        >
            <List data={data} columns={columns} />
        </Modal>
    );
}

export function ListIssueOverdueModal({ onClose }: { onClose: () => void }) {
    const { issuesStatus } = useOverview();

    const issuesOverdue = issuesStatus.filter(issue => {
        if (!issue.deadline) return false;
        return issue.status === 'opened' && isBefore(new Date(issue.deadline), new Date());
    });

    return (
        <IssueModal
            title="Chamados atrasado(s)"
            linkText="Acessar o módulo de chamados"
            linkUrl="/overview/issue"
            data={issuesOverdue}
            columns={COLUMNS}
            onClose={onClose}
        />
    );
}

export function ListIssuePendingModal({ onClose }: { onClose: () => void }) {
    const { issuesStatus } = useOverview();

    const issuesPending = issuesStatus.filter(issue => {
        if (!issue.deadline) return false;
        return issue.status === 'opened' && isBefore(new Date(), new Date(issue.deadline));
    });

    return (
        <IssueModal
            title="Chamados pendente(s)"
            linkText="Acessar o módulo de chamados"
            linkUrl="/overview/issue"
            data={issuesPending}
            columns={COLUMNS}
            onClose={onClose}
        />
    );
}


export function ListIssueInProgressModal({ onClose }: { onClose: () => void }) {
    const { issuesStatus } = useOverview();
    
    const issuesInProgress = issuesStatus.filter(issue => issue.status === 'in_progress');
        
    return (
        <IssueModal
            title="Chamados em andamento"
            linkText="Acessar o módulo de chamados"
            linkUrl="/overview/issue"
            data={issuesInProgress}
            columns={COLUMNS}
            onClose={onClose}
        />
    );
}


export function ListIssueWaitingOgmModal({ onClose }: { onClose: () => void }) {
    const { issuesStatus } = useOverview();
    useEffect(() => {
    }, [issuesStatus]);
    
    const issuesWaitingOgm = issuesStatus.filter(issue => issue.status === 'waiting_ogm');

    return (
        <IssueModal
            title="Chamados aguardando assembleia"
            linkText="Acessar o módulo de chamados"
            linkUrl="/overview/issue"
            data={issuesWaitingOgm}
            columns={COLUMNS}
            onClose={onClose}
        />
    );
}


export function ListIssueNoDeadlineModal({ onClose }: { onClose: () => void }) {
    const { issuesStatus } = useOverview();
    
    const issuesWithoutDeadline = issuesStatus.filter(issue => !issue.deadline && issue.status === 'opened');
    
    return (
        <IssueModal
            title="Chamados sem prazo informado"
            linkText="Acessar o módulo de chamados"
            linkUrl="/overview/issue"
            data={issuesWithoutDeadline}
            columns={COLUMNS}
            onClose={onClose}
        />
    );
}


